import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormInputWrapper } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { InputStyle, TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import osnGetAccountDetails from "@WhiteCap/Handlers/SubscriptionsDashboard/OsnGetAccountDetails";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => ({
    showAccountSelectionModal: state.WhiteCap.subscriptionsDashboardData.showAccountSelectionModal,
    accountSearchError: state.WhiteCap.subscriptionsDashboardData.accountSearchError,
    isSearchingAccount: state.WhiteCap.subscriptionsDashboardData.isSearchingAccount,
});

const mapDispatchToProps = { osnGetAccountDetails };

export interface SelectAccountModalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    bottomRowGridItem?: GridItemProps;
    proceedButton?: ButtonPresentationProps;
    gridItem?: GridItemProps;
    modalTitle?: TypographyProps;
    note?: TypographyProps;
    noteGridItem?: GridItemProps;
    searchCustomerText?: TextFieldPresentationProps;
}

export const selectAccountModalStyles: SelectAccountModalStyles = {
    modal: {
        size: 944,
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
                border-bottom: none;
                height: 50px;
            `,
            modalContent: css`
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 0px 30px 34px 30px;
                            `,
                            css`
                                padding: 0px 80px 50px 80px;
                            `,
                        ],
                        "min",
                    )}
            `,
            modalBody: css`
                height: 371px !important;
            `,
            modalContainer: css`
                width: 100%;
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-left: 8px;
                                margin-right: 8px;
                                height: auto;
                            `,
                            null,
                            css`
                                margin-left: 8px;
                                margin-right: 8px;
                            `,
                        ],
                        "min",
                    )}
            `,
            scrim: css`
                display: flex;
                align-items: center;
            `,
        },
    },
    container: { gap: 20 },
    bottomRowGridItem: {
        width: 12,
        css: css`
            gap: 1rem;
            justify-content: center;
        `,
    },
    proceedButton: {
        variant: "primary",
        css: css`
            width: auto;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            padding-top: 0;
        `,
    },
    noteGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    modalTitle: {
        variant: "h3",
        weight: 325,
        lineHeight: "40px",
        css: css`
            margin-bottom: 12px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            line-height: 30px;
                            font-size: 16px;
                        `,
                        null,
                        css`
                            line-height: 40px;
                            font-size: 20px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    note: {
        variant: "p",
        weight: 325,
        lineHeight: "25px",
        css: css`
            margin-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                            margin-top: 16px;
                        `,
                        null,
                        css`
                            font-size: 16px;
                            margin-top: 36px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    searchCustomerText: {
        cssOverrides: {
            formInputWrapper: css`
                text-align: center;
                margin-bottom: 4rem;
                min-height: 72.5px;
                > input {
                    width: 320px !important;
                }
            `,
        },
    },
};

const styles = selectAccountModalStyles;

const OsnAccountNumberModal = ({
    showAccountSelectionModal,
    osnGetAccountDetails,
    accountSearchError,
    isSearchingAccount,
}: Props) => {
    const dispatch = useDispatch();
    const [searchValue, setSearchValue] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (accountSearchError) {
            setErrorMessage(accountSearchError);
        }
    }, [accountSearchError]);

    const handleSearchChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.currentTarget.value);
        setErrorMessage(null);
    };

    const performSearchNow = () => {
        if (!searchValue.trim()) {
            setErrorMessage(translate("Please enter an account number."));
            return;
        }

        setErrorMessage(null);
        osnGetAccountDetails({
            searchText: searchValue.trim(),
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            performSearchNow();
        }
    };

    return (
        <Modal {...styles.modal} isOpen={showAccountSelectionModal} isCloseable={false} closeOnScrimClick={false}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.gridItem}>
                    <Typography {...styles.modalTitle}>
                        {translate("Enter account number you would like to manage subscriptions for")}
                    </Typography>
                </GridItem>

                <GridItem {...styles.noteGridItem}>
                    <TextField
                        {...styles.searchCustomerText}
                        value={searchValue}
                        onChange={handleSearchChanged}
                        onKeyDown={handleKeyDown}
                        placeholder={translate("Enter account number")}
                        error={errorMessage || undefined}
                    />
                </GridItem>

                <GridItem {...styles.bottomRowGridItem}>
                    <Button {...styles.proceedButton} onClick={performSearchNow} disabled={isSearchingAccount}>
                        {translate("Next")}
                    </Button>
                </GridItem>
            </GridContainer>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OsnAccountNumberModal);
